import { getUserIdCookie, LogSentryException } from "@shapr3d/frontend-lib";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { UMSrequestClient } from "../stores/requestClient";
import { CurrencySymbols, SubscriptionPeriod } from "../types";

export type PriceConfigResponseData = Array<{
  subscriptionPeriod: SubscriptionPeriod;
  price: number;
  currencyCode: string;
}>;

export type PriceConfig = {
  monthly: number;
  yearly: number;
  savingsPercent: number;
  currencySymbol: string;
  currencyCode: string;
  availablePeriods: SubscriptionPeriod[];
};

export const priceConfigQueryKey = ["prices"];

const getPriceConfig = async ({
  signal,
  userId,
}: {
  signal?: AbortSignal;
  userId: string | null;
}) => {
  const searchParams = new URLSearchParams();
  if (userId) {
    searchParams.append("user_id", userId);
  }
  const url = `/plans/web-prices?${searchParams.toString()}`;

  const response = await UMSrequestClient.publicRequest({
    method: "GET",
    url,
    retries: 5,
    abortSignal: signal,
  }).catch((error) => {
    LogSentryException({ messagePrefix: "Price config error", error });
    throw error;
  });

  const priceConfig = await response.json<PriceConfigResponseData>();

  if (priceConfig.some(({ currencyCode }) => CurrencySymbols.get(currencyCode) === undefined)) {
    const error = new Error("unknown currency symbol");
    LogSentryException({ messagePrefix: "Price config error", error });
    throw error;
  }

  return priceConfig;
};

export function usePriceConfig() {
  const userId = getUserIdCookie();
  const queryKey = [...priceConfigQueryKey, userId ?? "anon"];

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => getPriceConfig({ signal, userId }),
    staleTime: Infinity,
    select: (data): PriceConfig => {
      const monthlyConfig = data.find(
        (config) => config.subscriptionPeriod === SubscriptionPeriod.Monthly
      );
      const yearlyConfig = data.find(
        (config) => config.subscriptionPeriod === SubscriptionPeriod.Yearly
      );

      const savingsPercent =
        (1 - (yearlyConfig?.price ?? 0) / ((monthlyConfig?.price ?? 0) * 12)) * 100;

      return {
        monthly: (monthlyConfig?.price ?? 0) / 100,
        yearly: (yearlyConfig?.price ?? 0) / 100,
        savingsPercent: savingsPercent,
        currencySymbol: CurrencySymbols.get(yearlyConfig?.currencyCode ?? "USD") || "$",
        currencyCode: yearlyConfig?.currencyCode ?? "USD",
        availablePeriods: [SubscriptionPeriod.Monthly, SubscriptionPeriod.Yearly],
      };
    },
  });
}

export function usePrefetchPriceConfig() {
  const userId = getUserIdCookie();
  const queryKey = [...priceConfigQueryKey, userId ?? "anon"];
  const queryClient = useQueryClient();

  return queryClient.prefetchQuery({
    queryKey,
    queryFn: ({ signal }) => getPriceConfig({ signal, userId }),
    staleTime: Infinity,
  });
}
